import { ScrollPanel } from '@assets/component/Panel/ScrollPanel/ScrollPanel';
import { useStore, useTasksStore } from '@assets/model/Store';
import { getDatabase, onValue, ref } from 'firebase/database';
import { marked } from 'marked';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import moment from 'moment';
import * as style from './DealOutputs.module.less';
import { Button } from 'react-bootstrap';
import { generateVariablesOutputDownload } from '@assets/utils/Utils';

interface VariableData {
    variableName: string;
    humanReadableName: string;
    content: string;
    timestamp: string;
}

export const DealOutputs = observer(() => {
    const store = useStore();
    const tasksStore = useTasksStore();
    const [varsData, setVarsData] = useState<{ [varName: string]: string }>({});
    const [historyData, setHistoryData] = useState<any>({});
    const [expandedVars, setExpandedVars] = useState<Set<string>>(new Set());

    const companyId = store.selectedReportId;

    // Fetch variables from /flowtasks/vars/{companyId}
    useEffect(() => {
        if (!companyId) return;

        const varsPath = `flowtasks/vars/${companyId}`;
        const varsRef = ref(getDatabase(), varsPath);
        const unsubVars = onValue(varsRef, data => {
            setVarsData(data.val() || {});
        });

        return () => unsubVars();
    }, [companyId]);

    // Fetch history for timestamps from /flowtasks/history/{companyId}
    useEffect(() => {
        if (!companyId) return;

        const historyPath = `flowtasks/history/${companyId}`;
        const historyRef = ref(getDatabase(), historyPath);
        const unsubHistory = onValue(historyRef, data => {
            setHistoryData(data.val() || {});
        });

        return () => unsubHistory();
    }, [companyId]);

    // Variable name mapping
    const variableNameMapping: { [key: string]: string } = {
        v_identify_industry: 'Identify Industry',
        v_industry_history: 'Industry History',
        v_competitive_landscape: 'Competitive Landscape',
        v_growth_drivers: 'Growth Drivers',
        v_size_and_growth: 'Size & Growth'
    };

    // Get timestamp for a variable by finding its task via defs
    const getTimestampForVariable = (variableName: string): string => {
        // Find task that saves to this variable
        const taskDefs = tasksStore.tasksDefinitions;
        let taskId: string | null = null;

        for (const tid in taskDefs) {
            if (taskDefs[tid].save_to === variableName) {
                taskId = tid;
                break;
            }
        }

        if (!taskId || !historyData[taskId]) {
            return 'N/A';
        }

        // Get last result from history
        const taskHistory = historyData[taskId];
        const results = taskHistory.results;

        if (!results || results.length === 0) {
            return 'N/A';
        }

        // Get the most recent result (last in array)
        const lastResult = results[results.length - 1];
        const createdDate = lastResult.createdDate;

        if (!createdDate) {
            return 'N/A';
        }

        // Format as YYYY-MM-DD HH:mm:ss
        return moment(createdDate).format('YYYY-MM-DD HH:mm:ss');
    };

    // Toggle expand/collapse for a variable
    const toggleExpand = (variableName: string) => {
        setExpandedVars(prev => {
            const newSet = new Set(prev);
            if (newSet.has(variableName)) {
                newSet.delete(variableName);
            } else {
                newSet.add(variableName);
            }
            return newSet;
        });
    };

    // Build variable list dynamically from all variables in Firebase
    const variables: VariableData[] = [];

    for (const varName in varsData) {
        variables.push({
            variableName: varName,
            humanReadableName: variableNameMapping[varName] || varName,
            content: varsData[varName],
            timestamp: getTimestampForVariable(varName)
        });
    }

    // Sort by timestamp (most recent first)
    variables.sort((a, b) => {
        if (a.timestamp === 'N/A') return 1;
        if (b.timestamp === 'N/A') return -1;
        return (
            moment(b.timestamp, 'YYYY-MM-DD HH:mm:ss').valueOf() -
            moment(a.timestamp, 'YYYY-MM-DD HH:mm:ss').valueOf()
        );
    });

    const tableRows = variables.map(varData => {
        const isExpanded = expandedVars.has(varData.variableName);

        // Render full content or preview based on expansion state
        const contentToShow = isExpanded
            ? varData.content
            : varData.content.substring(0, 300);
        const rawHtml = marked.parse(contentToShow, { async: false });
        const sanitizedHtml = DOMPurify.sanitize(rawHtml);

        return (
            <tr key={varData.variableName} className={style.tableRow}>
                <td valign="top" className={style.moduleColumn}>
                    <div className={style.moduleName}>
                        {varData.humanReadableName}
                    </div>
                </td>
                <td className={style.outputColumn}>
                    <div className={style.outputContainer}>
                        <div
                            className={
                                isExpanded
                                    ? style.expandedContent
                                    : style.truncatedContent
                            }
                            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                        />
                        {!isExpanded && (
                            <div className={style.truncateIndicator}>...</div>
                        )}
                        <button
                            className={style.toggleButton}
                            onClick={() => toggleExpand(varData.variableName)}
                        >
                            {isExpanded ? '▲ Show less' : '▼ Show more'}
                        </button>
                    </div>
                </td>
                <td valign="top" className={style.dateColumn}>
                    {varData.timestamp}
                </td>
                <td valign="top" className={style.exportColumn}>
                    <Button
                        onClick={() => {
                            generateVariablesOutputDownload(companyId, [
                                varData.variableName
                            ]);
                        }}
                    >
                        Export
                    </Button>
                </td>
            </tr>
        );
    });

    return (
        <ScrollPanel>
            <div className={style.tableContainer}>
                <table className={style.outputsTable}>
                    <thead>
                        <tr className={style.headerRow}>
                            <th className={style.headerModule}>Module</th>
                            <th className={style.headerOutput}>Output</th>
                            <th className={style.headerDate}>Date</th>
                            <th className={style.headerExport}>Export</th>
                        </tr>
                    </thead>
                    <tbody>{tableRows}</tbody>
                </table>
            </div>
        </ScrollPanel>
    );
});
